import React, { useState, useEffect } from 'react';
import { enrollmentService, studentService, courseService } from '../services';

function isDuplicateEnrollment(enrollments, studentId, courseId, excludeEnrollmentId = null) {
  return enrollments.some((e) => {
    if (excludeEnrollmentId && e._id === excludeEnrollmentId) return false;
    const sid = e.student?._id || e.student;
    const cid = e.course?._id || e.course;
    return sid === studentId && cid === courseId;
  });
}

const EnrollmentManagement = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    student: '',
    students: [],
    course: '',
    enrollmentDate: new Date().toISOString().split('T')[0],
  });
  const [fieldOfStudyFilter, setFieldOfStudyFilter] = useState('');

  useEffect(() => { fetchEnrollments(); fetchStudents(); fetchCourses(); }, []);

  useEffect(() => {
    if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 4000); return () => clearTimeout(t); }
  }, [successMessage]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  const fetchEnrollments = async () => {
    try { setLoading(true); setError(''); const data = await enrollmentService.getAllEnrollments(); setEnrollments(data); }
    catch (err) { setError(err.error || err.message || 'Failed to fetch enrollments'); }
    finally { setLoading(false); }
  };

  const fetchStudents = async () => {
    try { const data = await studentService.getAllStudents(); setStudents(data); }
    catch (err) { console.error('Error loading students:', err); }
  };

  const fetchCourses = async () => {
    try { const data = await courseService.getAllCourses(); setCourses(data); }
    catch (err) { console.error('Error loading courses:', err); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    if (formData.students.length === 0) {
      setError('Please select at least one student.'); return;
    }
    const duplicates = formData.students.filter(studentId => isDuplicateEnrollment(enrollments, studentId, formData.course));
    if (duplicates.length > 0) {
      const dupNames = duplicates.map(id => {
        const studentObj = students.find(s => s._id === id);
        return studentObj ? studentObj.fullName : 'Student';
      }).join(', ');
      setError(`The following students are already enrolled in this course: ${dupNames}`);
      return;
    }
    try {
      await Promise.all(formData.students.map(studentId => 
        enrollmentService.createEnrollment({ student: studentId, course: formData.course, ...(formData.enrollmentDate && { enrollmentDate: formData.enrollmentDate }) })
      ));
      setSuccessMessage('Enrollment(s) created successfully!'); resetForm(); fetchEnrollments();
    } catch (err) { setError(err.error || err.message || 'Failed to create enrollment(s)'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    if (!selectedEnrollment) return;
    if (isDuplicateEnrollment(enrollments, formData.student, formData.course, selectedEnrollment._id)) {
      setError('This student is already enrolled in this course.'); return;
    }
    try {
      await enrollmentService.updateEnrollment(selectedEnrollment._id, { student: formData.student, course: formData.course, ...(formData.enrollmentDate && { enrollmentDate: formData.enrollmentDate }) });
      setSuccessMessage('Enrollment updated successfully!'); resetForm(); fetchEnrollments();
    } catch (err) { setError(err.error || err.message || 'Failed to update enrollment'); }
  };

  const handleDelete = async (enrollmentId) => {
    setError(''); setSuccessMessage('');
    try { await enrollmentService.deleteEnrollment(enrollmentId); setSuccessMessage('Enrollment removed.'); setDeleteConfirm(null); fetchEnrollments(); }
    catch (err) { setError(err.error || err.message || 'Failed to delete enrollment'); setDeleteConfirm(null); }
  };

  const handleEdit = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setFormData({
      student: enrollment.student?._id || enrollment.student || '',
      course: enrollment.course?._id || enrollment.course || '',
      enrollmentDate: enrollment.enrollmentDate?.split?.('T')[0] || new Date().toISOString().split('T')[0],
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ student: '', students: [], course: '', enrollmentDate: new Date().toISOString().split('T')[0] });
    setSelectedEnrollment(null); setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-filter students if a course is selected and has a field of study
    if (name === 'course' && !selectedEnrollment) {
      const courseObj = courses.find(c => c._id === value);
      if (courseObj && courseObj.fieldOfStudy) {
        setFieldOfStudyFilter(courseObj.fieldOfStudy);
      }
    }
  };

  const getStudentName = (e) => (!e.student ? '—' : typeof e.student === 'object' ? e.student.fullName : '—');
  const getCourseName = (e) => (!e.course ? '—' : typeof e.course === 'object' ? e.course.courseName : '—');
  const getStudentEmail = (e) => (e.student && typeof e.student === 'object' ? e.student.email || null : null);

  const filteredEnrollments = enrollments.filter((e) => {
    const term = searchTerm.toLowerCase();
    return getStudentName(e).toLowerCase().includes(term) || getCourseName(e).toLowerCase().includes(term);
  });

  // Stats
  const thisMonth = enrollments.filter(e => {
    const d = new Date(e.enrollmentDate || 0);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const uniqueStudents = new Set(enrollments.map(e => e.student?._id || e.student).filter(Boolean)).size;
  const uniqueCourses = new Set(enrollments.map(e => e.course?._id || e.course).filter(Boolean)).size;

  const isNew = (enrollment) => {
    const d = enrollment.enrollmentDate ? new Date(enrollment.enrollmentDate) : null;
    if (!d) return false;
    return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24) <= 7;
  };

  // Avatar helpers
  const avatarPalette = [
    'linear-gradient(135deg,#60A5FA,#A78BFA)',
    'linear-gradient(135deg,#F472B6,#FB923C)',
    'linear-gradient(135deg,#34D399,#60A5FA)',
    'linear-gradient(135deg,#FBBF24,#F472B6)',
    'linear-gradient(135deg,#A78BFA,#34D399)',
  ];
  const coursePalette = [
    'linear-gradient(135deg,#60A5FA,#3B82F6)',
    'linear-gradient(135deg,#A78BFA,#7C3AED)',
    'linear-gradient(135deg,#F472B6,#DB2777)',
    'linear-gradient(135deg,#34D399,#059669)',
    'linear-gradient(135deg,#FBBF24,#D97706)',
  ];
  const getColor = (name, palette) => palette[(name?.charCodeAt(0) || 0) % palette.length];
  const getInitials = (name) => name && name !== '—' ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';

  const selectedStudentObj = students.find(s => s._id === formData.student);
  const selectedStudentsForCreate = students.filter(s => formData.students.includes(s._id));
  const selectedCourseObj = courses.find(c => c._id === formData.course);

  // Field of Study helpers
  const uniqueFields = [...new Set(students.map(s => s.fieldOfStudy).filter(Boolean))].sort();
  const displayedStudents = students.filter(s => !fieldOfStudyFilter || s.fieldOfStudy === fieldOfStudyFilter);
  
  const handleSelectAll = (e) => {
    e.preventDefault();
    const studentsToAdd = displayedStudents.map(s => s._id);
    const combined = [...new Set([...formData.students, ...studentsToAdd])];
    setFormData(prev => ({ ...prev, students: combined }));
  };

  const handleDeselectAll = (e) => {
    e.preventDefault();
    const displayedIds = displayedStudents.map(s => s._id);
    const filtered = formData.students.filter(id => !displayedIds.includes(id));
    setFormData(prev => ({ ...prev, students: filtered }));
  };

  return (
    <>
      <style>{`
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .em-root {
          
          --primary-blue: #1e40af;
          --secondary-blue: #3b82f6;
          --accent-orange: #ea580c;
          --text-dark: #111827;
          --text-medium: #374151;
          --text-light: #6b7280;
          --border-light: #e5e7eb;
          --border-medium: #d1d5db;
          --background-gray: #f8fafc;
          --background-white: #ffffff;
          --success-green: #059669;
          --error-red: #dc2626;
          --warning-yellow: #d97706;
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          
          background: var(--background-gray);
          color: var(--text-dark);
          font-family: system-ui, -apple-system, 'Inter', sans-serif;
          min-height: 100vh;
          padding: 28px 24px;
        }

        /* ── TOAST ── */
        .em-toast {
          position: fixed; top: 20px; right: 20px; z-index: 1000;
          display: flex; align-items: center; gap: 10px;
          padding: 14px 20px; border-radius: 6px;
          font-size: 13px; font-weight: 600;
          box-shadow: var(--shadow-md);
          animation: emSlideIn 0.3s ease; max-width: 380px; pointer-events: none;
        }
        .em-toast-success { background: #f0fdf4; border: 1px solid #d1fae5; color: var(--success-green); border-left: 4px solid var(--success-green); }
        .em-toast-error   { background: #fef2f2; border: 1px solid #fecaca; color: var(--error-red); border-left: 4px solid var(--error-red); }
        @keyframes emSlideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }

        /* ── PAGE HEADER ── */
        .em-page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 20px; margin-bottom: 28px; flex-wrap: wrap;
        }
        .em-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(52,211,153,0.08); border: 1px solid rgba(52,211,153,0.2);
          border-radius: 12px; padding: 4px 12px;
          font-size: 11px; font-weight: 700; color: var(--accent);
          letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 10px;
        }
        .em-page-title {
          font-family: inherit;
          font-size: 28px; font-weight: 800; letter-spacing: -0.5px; line-height: 1.1;
        }
        .em-page-title span {
          background: linear-gradient(90deg, var(--accent), var(--accent2));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .em-page-sub { font-size: 13px; color: var(--text-light); margin-top: 6px; }

        /* Stats row */
        .em-stats-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-start; }
        .em-stat {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 12px 18px; text-align: center; min-width: 80px;
          position: relative; overflow: hidden;
        }
        .em-stat::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: var(--c, var(--accent));
        }
        .em-stat-num {
          font-family: inherit; font-size: 22px; font-weight: 800;
          background: linear-gradient(135deg, var(--text), var(--c, var(--accent)));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .em-stat-label { font-size: 10px; color: var(--text-light); font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }

        /* ── TOOLBAR ── */
        .em-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
        .em-search {
          flex: 1; min-width: 200px; max-width: 440px;
          display: flex; align-items: center; gap: 10px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 11px 16px;
          transition: border-color 0.2s;
        }
        .em-search:focus-within { border-color: var(--border-hi); }
        .em-search-icon { font-size: 15px; color: var(--text-light); flex-shrink: 0; }
        .em-search input {
          flex: 1; border: none; outline: none; background: none;
          font-size: 13px; color: var(--text-dark); font-family: inherit;
        }
        .em-search input::placeholder { color: var(--text-light); }
        .em-clear { background: none; border: none; color: var(--text-light); cursor: pointer; font-size: 15px; padding: 0 2px; transition: color 0.2s; }
        .em-clear:hover { color: var(--text-dark); }

        .em-btn-add {
          display: flex; align-items: center; gap: 8px; padding: 11px 20px;
          background: linear-gradient(135deg, var(--accent), var(--accent2));
          color: white; border: none; border-radius: 6px;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .em-btn-add:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .em-btn-add.cancel {
          background: rgba(248,113,113,0.1); color: var(--error-red);
          border: 1px solid rgba(248,113,113,0.3); box-shadow: none;
        }
        .em-btn-add.cancel:hover { background: rgba(248,113,113,0.15); transform: none; }

        .em-btn-refresh {
          display: flex; align-items: center; gap: 6px; padding: 11px 16px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; color: var(--muted2);
          font-size: 13px; font-weight: 500; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .em-btn-refresh:hover { border-color: var(--border-light); color: var(--text-dark); }

        /* ── FORM PANEL ── */
        .em-form-panel {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; margin-bottom: 28px; overflow: hidden;
          animation: emExpandIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes emExpandIn { from { opacity:0; transform:scaleY(0.95) translateY(-8px); transform-origin:top; } to { opacity:1; transform:scaleY(1) translateY(0); } }

        .em-form-header {
          display: flex; align-items: center; gap: 14px;
          padding: 20px 28px; border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(52,211,153,0.06), rgba(96,165,250,0.04));
        }
        .em-form-icon {
          width: 38px; height: 38px; border-radius: 8px;
          background: linear-gradient(135deg, var(--accent), var(--accent2));
          display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
        }
        .em-form-title { font-family: inherit; font-size: 17px; font-weight: 700; }
        .em-form-sub { font-size: 12px; color: var(--text-light); margin-top: 2px; }

        .em-form-body { padding: 28px; display: flex; flex-direction: column; gap: 22px; }

        /* Preview pairing strip */
        .em-pairing {
          display: flex; align-items: center; gap: 12px;
          padding: 16px 18px;
          background: rgba(52,211,153,0.05);
          border: 1px solid rgba(52,211,153,0.15);
          border-radius: 6px;
        }
        .em-pair-chip {
          flex: 1; display: flex; align-items: center; gap: 10px;
          padding: 10px 14px;
          background: var(--surface2); border-radius: 6px;
          border: 1px solid var(--border-light); min-width: 0;
        }
        .em-pair-chip.empty { opacity: 0.4; font-size: 12px; color: var(--text-light); gap: 8px; }
        .em-pair-av {
          width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white; font-family: inherit;
        }
        .em-pair-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .em-pair-sub { font-size: 11px; color: var(--text-light); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .em-pair-arrow {
          font-size: 20px; color: var(--accent); flex-shrink: 0; font-weight: 700;
        }

        .em-field { display: flex; flex-direction: column; gap: 8px; }
        .em-label {
          font-size: 11px; font-weight: 700; color: var(--muted2);
          text-transform: uppercase; letter-spacing: 0.6px;
          display: flex; align-items: center; gap: 4px;
        }
        .em-req { color: var(--accent); font-size: 14px; line-height: 1; }

        .em-select, .em-input {
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 13px 16px;
          font-size: 14px; color: var(--text-dark);
          font-family: inherit; outline: none; width: 100%;
          transition: border-color 0.2s, background 0.2s;
        }
        .em-select:focus, .em-input:focus { border-color: rgba(52,211,153,0.4); background: rgba(52,211,153,0.03); }
        .em-select {
          cursor: pointer; appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 14px center;
          padding-right: 36px;
        }
        .em-select option { background: #161B2E; color: var(--text-dark); }

        .em-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }

        .em-form-footer {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 10px; padding: 20px 28px; border-top: 1px solid var(--border);
        }
        .em-btn-submit {
          display: flex; align-items: center; gap: 8px; padding: 12px 28px;
          background: linear-gradient(135deg, var(--accent), var(--accent2));
          color: white; border: none; border-radius: 6px;
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
        }
        .em-btn-submit:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .em-btn-cancel {
          padding: 12px 22px; background: var(--border-light);
          border: 1px solid var(--border-light); border-radius: 6px;
          color: var(--muted2); font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .em-btn-cancel:hover { background: var(--border-light); color: var(--text-dark); }

        /* ── SECTION HEADER ── */
        .em-section-head {
          display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
        }
        .em-section-title {
          font-family: inherit; font-size: 17px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .em-section-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 8px var(--accent); }
        .em-count-badge {
          background: rgba(52,211,153,0.12); border: 1px solid rgba(52,211,153,0.2);
          color: var(--accent); border-radius: 12px; padding: 2px 10px; font-size: 12px; font-weight: 700;
        }
        .em-search-hint { font-size: 12px; color: var(--text-light); }
        .em-search-hint span { color: var(--accent); }

        /* ── TABLE ── */
        .em-table-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; overflow: hidden;
        }
        .em-scroll { overflow-x: auto; }
        .em-scroll::-webkit-scrollbar { height: 4px; }
        .em-scroll::-webkit-scrollbar-thumb { background: var(--border-light); border-radius: 6px; }
        .em-table { width: 100%; border-collapse: collapse; min-width: 580px; }
        .em-table th {
          padding: 13px 20px; font-size: 10px; font-weight: 800; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 1px;
          border-bottom: 1px solid var(--border); text-align: left;
          background: var(--border-light); white-space: nowrap;
        }
        .em-table td {
          padding: 14px 20px; font-size: 13px; color: var(--text-dark);
          border-bottom: 1px solid var(--border-light); vertical-align: middle;
        }
        .em-table tbody tr { transition: background 0.15s; }
        .em-table tbody tr:hover { background: rgba(52,211,153,0.03); }
        .em-table tbody tr:last-child td { border-bottom: none; }

        /* Cell types */
        .em-student-cell, .em-course-cell { display: flex; align-items: center; gap: 10px; }
        .em-cell-av {
          width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white; font-family: inherit;
        }
        .em-cell-name { font-weight: 600; font-size: 13px; }
        .em-cell-email { font-size: 11px; color: var(--text-light); margin-top: 1px; }

        .em-date-cell { display: flex; flex-direction: column; gap: 2px; }
        .em-date-main { font-size: 13px; font-weight: 500; }
        .em-date-rel { font-size: 11px; color: var(--text-light); }

        .em-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 12px;
          font-size: 11px; font-weight: 600; letter-spacing: 0.2px; white-space: nowrap;
        }
        .em-badge-new  { background: rgba(244,114,182,0.12); color: #F9A8D4; border: 1px solid rgba(244,114,182,0.25); }
        .em-badge-ok   { background: rgba(52,211,153,0.1);  color: #6EE7B7; border: 1px solid rgba(52,211,153,0.2); }

        .em-td-actions { display: flex; gap: 6px; }
        .em-btn-edit, .em-btn-del {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 7px 12px; border-radius: 6px;
          font-size: 12px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 0.2s; border: 1px solid; white-space: nowrap;
        }
        .em-btn-edit { background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.2); color: #60A5FA; }
        .em-btn-edit:hover { background: rgba(96,165,250,0.15); border-color: rgba(96,165,250,0.4); }
        .em-btn-del  { background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.2); color: var(--error-red); }
        .em-btn-del:hover { background: rgba(248,113,113,0.15); border-color: rgba(248,113,113,0.4); }

        /* ── MOBILE CARDS ── */
        .em-cards { display: none; flex-direction: column; gap: 12px; padding: 16px; }
        .em-card {
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 0; overflow: hidden;
          animation: emFadeUp 0.3s ease both; transition: border-color 0.2s;
        }
        .em-card:hover { border-color: var(--border-light); }
        @keyframes emFadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }

        .em-card-strip {
          display: flex; align-items: stretch; gap: 0;
        }
        .em-card-student-col {
          flex: 1; padding: 16px; display: flex; align-items: center; gap: 10px;
          border-right: 1px solid var(--border);
        }
        .em-card-course-col {
          flex: 1; padding: 16px; display: flex; align-items: center; gap: 10px;
        }
        .em-card-col-av {
          width: 38px; height: 38px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800; color: white; font-family: inherit;
        }
        .em-card-col-label { font-size: 10px; color: var(--text-light); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
        .em-card-col-name { font-size: 13px; font-weight: 600; line-height: 1.3; }

        .em-card-foot {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 16px; border-top: 1px solid var(--border);
          background: var(--border-light); flex-wrap: wrap; gap: 8px;
        }
        .em-card-date { font-size: 12px; color: var(--text-light); display: flex; align-items: center; gap: 6px; }
        .em-card-actions { display: flex; gap: 6px; }

        /* ── EMPTY / LOADING ── */
        .em-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 72px 24px; gap: 12px;
        }
        .em-empty-icon { font-size: 52px; opacity: 0.25; }
        .em-empty-title { font-family: inherit; font-size: 18px; font-weight: 700; color: var(--text-light); }
        .em-empty-sub { font-size: 13px; color: var(--text-light); text-align: center; }

        .em-loading { display: flex; align-items: center; justify-content: center; padding: 72px; gap: 8px; }
        .em-dot { width: 8px; height: 8px; border-radius: 50%; animation: emBounce 1.2s ease-in-out infinite; }
        .em-dot:nth-child(1) { background: var(--accent); }
        .em-dot:nth-child(2) { background: var(--accent2); animation-delay: 0.2s; }
        .em-dot:nth-child(3) { background: var(--error-red); animation-delay: 0.4s; }
        @keyframes emBounce { 0%,80%,100% { transform:scale(0.6); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }

        /* ── DELETE MODAL ── */
        .em-modal-overlay {
          position: fixed; inset: 0; z-index: 200;
          background: rgba(0,0,0,0.72); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
          animation: emFadeIn 0.2s ease;
        }
        @keyframes emFadeIn { from { opacity:0; } to { opacity:1; } }
        .em-modal {
          background: var(--background-white); border: 1px solid rgba(248,113,113,0.2);
          border-radius: 12px; padding: 32px; max-width: 440px; width: 100%;
          box-shadow: var(--shadow-md);
          animation: emModalIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes emModalIn { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }
        .em-modal-icon {
          width: 52px; height: 52px; border-radius: 12px;
          background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25);
          display: flex; align-items: center; justify-content: center; font-size: 24px; margin-bottom: 18px;
        }
        .em-modal-title { font-family: inherit; font-size: 18px; font-weight: 700; margin-bottom: 8px; }
        .em-modal-sub { font-size: 13px; color: var(--text-light); margin-bottom: 20px; line-height: 1.6; }
        .em-modal-pair {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 16px; margin-bottom: 24px;
          background: var(--border-light); border: 1px solid var(--border-light); border-radius: 6px;
        }
        .em-modal-pair-av {
          width: 32px; height: 32px; border-radius: 6px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white; font-family: inherit;
        }
        .em-modal-pair-arrow { font-size: 16px; color: var(--text-light); flex-shrink: 0; margin: 0 4px; }
        .em-modal-pair-text { font-size: 13px; font-weight: 600; }
        .em-modal-pair-sub { font-size: 11px; color: var(--text-light); }
        .em-modal-actions { display: flex; gap: 10px; }
        .em-modal-del {
          flex: 1; padding: 12px;
          background: rgba(248,113,113,0.12); border: 1px solid rgba(248,113,113,0.3);
          border-radius: 6px; color: var(--error-red);
          font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .em-modal-del:hover { background: rgba(248,113,113,0.2); }
        .em-modal-keep {
          flex: 1; padding: 12px;
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; color: var(--muted2);
          font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .em-modal-keep:hover { color: var(--text-dark); }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .em-root { padding: 16px; }
          .em-page-title { font-size: 22px; }
          .em-form-body { padding: 20px; }
          .em-form-footer { padding: 16px 20px; }
          .em-form-header { padding: 16px 20px; }
          .em-stats-row { display: none; }
          .em-field-row { grid-template-columns: 1fr; }
          .em-table-wrap { display: none; }
          .em-cards { display: flex; }
          .em-pairing { flex-direction: column; }
          .em-pair-arrow { transform: rotate(90deg); }
        }
        @media (max-width: 480px) {
          .em-toolbar { flex-direction: column; align-items: stretch; }
          .em-search { max-width: none; }
          .em-btn-add, .em-btn-refresh { justify-content: center; }
        }
      `}</style>

      {successMessage && <div className="em-toast em-toast-success">✓ {successMessage}</div>}
      {error && <div className="em-toast em-toast-error">⚠ {error}</div>}

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="em-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="em-modal" onClick={e => e.stopPropagation()}>
            <div className="em-modal-icon">📋</div>
            <div className="em-modal-title">Remove Enrollment?</div>
            <div className="em-modal-sub">This will permanently remove the following enrollment record.</div>
            <div className="em-modal-pair">
              <div className="em-modal-pair-av" style={{ background: getColor(getStudentName(deleteConfirm), avatarPalette) }}>
                {getInitials(getStudentName(deleteConfirm))}
              </div>
              <div>
                <div className="em-modal-pair-text">{getStudentName(deleteConfirm)}</div>
                <div className="em-modal-pair-sub">Student</div>
              </div>
              <div className="em-modal-pair-arrow">→</div>
              <div className="em-modal-pair-av" style={{ background: getColor(getCourseName(deleteConfirm), coursePalette) }}>
                {getInitials(getCourseName(deleteConfirm))}
              </div>
              <div>
                <div className="em-modal-pair-text">{getCourseName(deleteConfirm)}</div>
                <div className="em-modal-pair-sub">Course</div>
              </div>
            </div>
            <div className="em-modal-actions">
              <button className="em-modal-keep" onClick={() => setDeleteConfirm(null)}>Keep It</button>
              <button className="em-modal-del" onClick={() => handleDelete(deleteConfirm._id)}>Remove</button>
            </div>
          </div>
        </div>
      )}

      <div className="em-root">
        {/* Page Header */}
        <div className="em-page-header">
          <div>
            <div className="em-eyebrow">✦ Admin Panel</div>
            <h1 className="em-page-title">Enrollment <span>Management</span></h1>
            <p className="em-page-sub">Link students to courses and track enrollment dates</p>
          </div>
          <div className="em-stats-row">
            <div className="em-stat" style={{ '--c': '#34D399' }}>
              <div className="em-stat-num">{enrollments.length}</div>
              <div className="em-stat-label">Total</div>
            </div>
            <div className="em-stat" style={{ '--c': '#60A5FA' }}>
              <div className="em-stat-num">{uniqueStudents}</div>
              <div className="em-stat-label">Students</div>
            </div>
            <div className="em-stat" style={{ '--c': '#A78BFA' }}>
              <div className="em-stat-num">{uniqueCourses}</div>
              <div className="em-stat-label">Courses</div>
            </div>
            <div className="em-stat" style={{ '--c': '#F472B6' }}>
              <div className="em-stat-num">{thisMonth}</div>
              <div className="em-stat-label">This Month</div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="em-toolbar">
          <div className="em-search">
            <span className="em-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by student or course name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="em-clear" onClick={() => setSearchTerm('')}>✕</button>}
          </div>
          <button
            className={`em-btn-add${showForm ? ' cancel' : ''}`}
            onClick={() => { if (showForm) { resetForm(); } else { setShowForm(true); } }}
          >
            {showForm ? '✕  Cancel' : '+ New Enrollment'}
          </button>
          <button className="em-btn-refresh" onClick={fetchEnrollments}>↻ Refresh</button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="em-modal-overlay" onClick={resetForm}>
            <div className="em-form-panel" onClick={e => e.stopPropagation()}>
              <div className="em-form-header">
                <div className="em-form-icon">{selectedEnrollment ? '✏️' : '📋'}</div>
                <div>
                  <div className="em-form-title">{selectedEnrollment ? 'Edit Enrollment' : 'New Enrollment'}</div>
                  <div className="em-form-sub">Select a student and course to link them together</div>
                </div>
              </div>

              <form onSubmit={selectedEnrollment ? handleUpdate : handleCreate}>
                <div className="em-form-body">

                {/* Live pairing preview */}
                <div className="em-pairing">
                  <div className={`em-pair-chip${(!selectedEnrollment && selectedStudentsForCreate.length === 0) || (selectedEnrollment && !selectedStudentObj) ? ' empty' : ''}`}>
                    {selectedEnrollment ? (
                      selectedStudentObj ? (
                        <>
                          <div className="em-pair-av" style={{ background: getColor(selectedStudentObj.fullName, avatarPalette) }}>
                            {getInitials(selectedStudentObj.fullName)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="em-pair-name">{selectedStudentObj.fullName}</div>
                            {selectedStudentObj.email && <div className="em-pair-sub">{selectedStudentObj.email}</div>}
                          </div>
                        </>
                      ) : (
                        <><span>👤</span><span>No student selected</span></>
                      )
                    ) : (
                      selectedStudentsForCreate.length > 0 ? (
                        <>
                          <div className="em-pair-av" style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent2))' }}>
                            {selectedStudentsForCreate.length}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="em-pair-name">{selectedStudentsForCreate.length} Students Selected</div>
                            <div className="em-pair-sub">Multiple selections</div>
                          </div>
                        </>
                      ) : (
                        <><span>👤</span><span>No student selected</span></>
                      )
                    )}
                  </div>
                  <div className="em-pair-arrow">→</div>
                  <div className={`em-pair-chip${!selectedCourseObj ? ' empty' : ''}`}>
                    {selectedCourseObj ? (
                      <>
                        <div className="em-pair-av" style={{ background: getColor(selectedCourseObj.courseName, coursePalette) }}>
                          {getInitials(selectedCourseObj.courseName)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="em-pair-name">{selectedCourseObj.courseName}</div>
                          <div className="em-pair-sub">Course</div>
                        </div>
                      </>
                    ) : (
                      <><span>📚</span><span>No course selected</span></>
                    )}
                  </div>
                </div>

                <div className="em-field-row">
                  <div className="em-field">
                    <label className="em-label">{selectedEnrollment ? 'Student' : 'Students'} <span className="em-req">*</span></label>
                    {selectedEnrollment ? (
                      <select className="em-select" name="student" value={formData.student} onChange={handleInputChange} required>
                        <option value="">— Select student —</option>
                        {students.map(s => (
                          <option key={s._id} value={s._id}>{s.fullName}{s.email ? ` (${s.email})` : ''}</option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                          <select 
                            className="em-select" 
                            style={{ flex: 1, padding: '8px 12px' }}
                            value={fieldOfStudyFilter} 
                            onChange={(e) => setFieldOfStudyFilter(e.target.value)}
                          >
                            <option value="">— All Fields of Study —</option>
                            {uniqueFields.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                          <button type="button" onClick={handleSelectAll} style={{ padding: '8px 12px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', color: 'var(--accent)', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>SELECT ALL</button>
                          <button type="button" onClick={handleDeselectAll} style={{ padding: '8px 12px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: 'var(--error-red)', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>CLEAR</button>
                        </div>
                        <div className="em-multi-select" style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', padding: '10px', maxHeight: '180px', overflowY: 'auto' }}>
                          {students.length === 0 ? (
                            <div style={{ fontSize: '13px', color: 'var(--muted)', textAlign: 'center', padding: '10px' }}>Loading students...</div>
                          ) : displayedStudents.length === 0 ? (
                            <div style={{ fontSize: '13px', color: 'var(--muted)', textAlign: 'center', padding: '10px' }}>No students in this field</div>
                          ) : (
                            displayedStudents.map(s => (
                              <label key={s._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer', borderRadius: '4px', borderBottom: '1px solid var(--border-light)' }}>
                                <input 
                                  type="checkbox" 
                                  checked={formData.students.includes(s._id)} 
                                  onChange={(e) => {
                                    const newStudents = e.target.checked 
                                      ? [...formData.students, s._id] 
                                      : formData.students.filter(id => id !== s._id);
                                    setFormData(prev => ({ ...prev, students: newStudents }));
                                  }}
                                  style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                                  {s.fullName} {s.email && <span style={{ color: 'var(--muted)', fontSize: '11px' }}>({s.email})</span>}
                                  {s.fieldOfStudy && !fieldOfStudyFilter && <span style={{ marginLeft: 'auto', fontSize: '10px', background: 'var(--border-light)', padding: '2px 6px', borderRadius: '10px', color: 'var(--muted2)' }}>{s.fieldOfStudy}</span>}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="em-field">
                    <label className="em-label">Course <span className="em-req">*</span></label>
                    <select className="em-select" name="course" value={formData.course} onChange={handleInputChange} required>
                      <option value="">— Select course —</option>
                      {courses.map(c => (
                        <option key={c._id} value={c._id}>{c.courseName}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="em-field" style={{ maxWidth: '280px' }}>
                  <label className="em-label">Enrollment Date</label>
                  <input className="em-input" type="date" name="enrollmentDate" value={formData.enrollmentDate} onChange={handleInputChange} />
                </div>
                </div>

                <div className="em-form-footer">
                  <button type="button" className="em-btn-cancel" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="em-btn-submit">
                    {selectedEnrollment ? '✓ Update Enrollment' : '✓ Create Enrollment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Table section */}
        <div className="em-section-head">
          <div className="em-section-title">
            <span className="em-section-dot" />
            All Enrollments
            <span className="em-count-badge">{filteredEnrollments.length}</span>
          </div>
          {searchTerm && (
            <span className="em-search-hint">Results for "<span>{searchTerm}</span>"</span>
          )}
        </div>

        {loading ? (
          <div className="em-loading"><div className="em-dot"/><div className="em-dot"/><div className="em-dot"/></div>
        ) : filteredEnrollments.length === 0 ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px' }}>
            <div className="em-empty">
              <span className="em-empty-icon">📋</span>
              <div className="em-empty-title">{searchTerm ? 'No enrollments found' : 'No enrollments yet'}</div>
              <div className="em-empty-sub">{searchTerm ? 'Try a different search term' : 'Click "+ New Enrollment" to link a student to a course'}</div>
            </div>
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px', overflow: 'hidden' }}>
            {/* Desktop Table */}
            <div className="em-scroll em-table-wrap">
              <table className="em-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Enrolled On</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnrollments.map((e, i) => {
                    const sName = getStudentName(e);
                    const cName = getCourseName(e);
                    const sEmail = getStudentEmail(e);
                    const newEnroll = isNew(e);
                    const dateStr = e.enrollmentDate ? new Date(e.enrollmentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    return (
                      <tr key={e._id} style={{ animationDelay: `${i * 30}ms` }}>
                        <td>
                          <div className="em-student-cell">
                            <div className="em-cell-av" style={{ background: getColor(sName, avatarPalette) }}>
                              {getInitials(sName)}
                            </div>
                            <div>
                              <div className="em-cell-name">{sName}</div>
                              {sEmail && <div className="em-cell-email">{sEmail}</div>}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="em-course-cell">
                            <div className="em-cell-av" style={{ background: getColor(cName, coursePalette) }}>
                              {getInitials(cName)}
                            </div>
                            <div className="em-cell-name">{cName}</div>
                          </div>
                        </td>
                        <td>
                          <div className="em-date-cell">
                            <span className="em-date-main">{dateStr}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`em-badge ${newEnroll ? 'em-badge-new' : 'em-badge-ok'}`}>
                            {newEnroll ? '🆕 New' : '✓ Enrolled'}
                          </span>
                        </td>
                        <td>
                          <div className="em-td-actions">
                            <button className="em-btn-edit" onClick={() => handleEdit(e)}>✏ Edit</button>
                            <button className="em-btn-del" onClick={() => setDeleteConfirm(e)}>🗑 Remove</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="em-cards">
              {filteredEnrollments.map((e, i) => {
                const sName = getStudentName(e);
                const cName = getCourseName(e);
                const newEnroll = isNew(e);
                const dateStr = e.enrollmentDate ? new Date(e.enrollmentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                return (
                  <div key={e._id} className="em-card" style={{ animationDelay: `${i * 40}ms` }}>
                    <div className="em-card-strip">
                      <div className="em-card-student-col">
                        <div className="em-card-col-av" style={{ background: getColor(sName, avatarPalette) }}>
                          {getInitials(sName)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="em-card-col-label">Student</div>
                          <div className="em-card-col-name">{sName}</div>
                        </div>
                      </div>
                      <div className="em-card-course-col">
                        <div className="em-card-col-av" style={{ background: getColor(cName, coursePalette) }}>
                          {getInitials(cName)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="em-card-col-label">Course</div>
                          <div className="em-card-col-name">{cName}</div>
                        </div>
                      </div>
                    </div>
                    <div className="em-card-foot">
                      <div className="em-card-date">
                        <span>📅</span>
                        {dateStr}
                        <span className={`em-badge ${newEnroll ? 'em-badge-new' : 'em-badge-ok'}`} style={{ marginLeft: '4px' }}>
                          {newEnroll ? 'New' : 'Enrolled'}
                        </span>
                      </div>
                      <div className="em-card-actions">
                        <button className="em-btn-edit" onClick={() => handleEdit(e)}>✏ Edit</button>
                        <button className="em-btn-del" onClick={() => setDeleteConfirm(e)}>🗑</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EnrollmentManagement;